import { Trans } from '@lingui/macro'
import { useWeb3React } from '@web3-react/core'
import { ButtonError, ButtonLight } from 'components/Button'
import { AutoColumn } from 'components/Column'
import Modal from 'components/Modal'
import { RowBetween } from 'components/Row'
import { AlertTriangle, ExternalLink as ExternalLinkIcon } from 'react-feather'
import styled from 'styled-components/macro'
import { CloseIcon, ThemedText } from 'theme'
import { ExplorerDataType, getExplorerLink } from 'utils/getExplorerLink'

const Wrapper = styled.div`
  width: 100%;
  padding: 16px;
`

const WarningIconContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 16px 0 2px;
`

const WarningIcon = styled(AlertTriangle)`
  width: 56px;
  height: 56px;
  stroke: ${({ theme }) => theme.accentWarning};
  stroke-width: 1.5;
`

const AddressBox = styled.div`
  background-color: ${({ theme }) => theme.backgroundSurface};
  border-radius: 36px;
  padding: 12px;
  margin: 10px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid ${({ theme }) => theme.textPiteas};
  gap: 12px;
`

const Address = styled(ThemedText.BodySmall)`
  font-size: 14px;
  word-break: break-all;
  color: ${({ theme }) => theme.textPrimary};
  font-family: monospace;
  font-weight: 600;
`

const ExternalLink = styled.a`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.accentAction};
  text-decoration: none;
  min-width: 20px;

  &:hover {
    opacity: 0.7;
  }
`

const WarningList = styled.ul`
  margin: 2px 0;
  padding-left: 20px;
  list-style: none;
`

const WarningListItem = styled.li`
  color: ${({ theme }) => theme.textSecondary};
  font-size: 13px;
  line-height: 1.2;
  margin-bottom: 6px;

  &:last-child {
    margin-bottom: 0;
  }

  &::before {
    content: '•';
    color: ${({ theme }) => theme.accentWarning};
    font-weight: bold;
    font-size: 16px;
    margin-right: 10px;
  }
`

const ButtonRow = styled(RowBetween)`
  gap: 12px;
  margin-top: 10px;
`

interface ReceiverWarningModalProps {
  isOpen: boolean
  onDismiss: () => void
  onConfirm: () => void
  recipientAddress: string
}

export default function ReceiverWarningModal({
  isOpen,
  onDismiss,
  onConfirm,
  recipientAddress,
}: ReceiverWarningModalProps) {
  const { chainId } = useWeb3React()

  const explorerLink = chainId ? getExplorerLink(chainId, recipientAddress, ExplorerDataType.ADDRESS) : undefined

  return (
    <Modal isOpen={isOpen} onDismiss={onDismiss} maxHeight={90}>
      <Wrapper>
        <AutoColumn gap="md">
          <RowBetween>
            <ThemedText.SubHeader>
              <Trans>Recipient Address Confirmation</Trans>
            </ThemedText.SubHeader>
            <CloseIcon onClick={onDismiss} />
          </RowBetween>

          <AutoColumn gap="md">
            <WarningIconContainer>
              <WarningIcon />
            </WarningIconContainer>

            <ThemedText.HeadlineSmall textAlign="center" fontWeight={600}>
              <Trans>You are sending to a different address</Trans>
            </ThemedText.HeadlineSmall>

            <ThemedText.BodySmall textAlign="center" lineHeight="1.5" color="textSecondary">
              <Trans>
                The swapped tokens will be sent to the recipient address below, NOT to your connected wallet.
              </Trans>
            </ThemedText.BodySmall>

            <AddressBox>
              <Address>{recipientAddress}</Address>
              {explorerLink && (
                <ExternalLink href={explorerLink} target="_blank" rel="noopener noreferrer">
                  <ExternalLinkIcon size={16} />
                </ExternalLink>
              )}
            </AddressBox>

            <AutoColumn gap="sm" style={{ width: '100%' }}>
              <ThemedText.SubHeaderSmall color="textPrimary" fontWeight={600}>
                <Trans>Please confirm:</Trans>
              </ThemedText.SubHeaderSmall>
              <WarningList>
                <WarningListItem>
                  <Trans>
                    Ensure you have <strong>carefully verified the recipient address</strong> above.
                  </Trans>
                </WarningListItem>
                <WarningListItem>
                  <Trans>
                    The address you pasted <strong>may be a token or protocol contract</strong>. Verify it is NOT a
                    contract address.
                  </Trans>
                </WarningListItem>
                <WarningListItem>
                  <Trans>
                    The output tokens will be <strong>sent directly to the specified address</strong>. This transaction
                    is <strong>irreversible</strong>!
                  </Trans>
                </WarningListItem>
                <WarningListItem>
                  <Trans>
                    Make sure you have <strong>read and understood</strong> all the points above.
                  </Trans>
                </WarningListItem>
              </WarningList>
            </AutoColumn>

            <ThemedText.Caption color="textTertiary" textAlign="center" fontSize="12px" style={{ marginTop: '0px' }}>
              <Trans>
                This feature is for experienced users. If unsure, cancel and send tokens after the swap instead.
              </Trans>
            </ThemedText.Caption>

            <ButtonRow>
              <ButtonLight onClick={onDismiss} style={{ flex: 1, padding: '12px 16px', fontSize: '14px' }}>
                <Trans>Cancel</Trans>
              </ButtonLight>
              <ButtonError onClick={onConfirm} style={{ flex: 1, padding: '12px 16px', fontSize: '14px' }}>
                <Trans>I Accept the Risks</Trans>
              </ButtonError>
            </ButtonRow>
          </AutoColumn>
        </AutoColumn>
      </Wrapper>
    </Modal>
  )
}
